/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { BodyType, ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  CreateViewParityCheckPathParameters,
  ListTransformationOperationsParams,
  RunSingleTransformationOperationPathParameters,
  RunViewTransformationWorkflowPathParameters,
  TLDispatchViewWorkflowResponse,
  TLHTTPValidationError,
  TLOperationCatalogResponse,
  TLRunOperationRequest,
  TLRunOperationResponse,
  TLRunViewWorkflowRequest,
  TLViewParityCheckRequest,
  TLViewParityCheckResponse,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary List Transformation Operations
 */
export const listTransformationOperations = (
  params?: ListTransformationOperationsParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLOperationCatalogResponse>(
    { url: `/view-remediation/transformations/operations`, method: 'GET', params, signal },
    options,
  );
};

export const getListTransformationOperationsQueryKey = (params?: ListTransformationOperationsParams) => {
  return ['view-remediation', 'transformations', 'operations', ...(params ? [params] : [])] as const;
};

export const getListTransformationOperationsQueryOptions = <
  TData = Awaited<ReturnType<typeof listTransformationOperations>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListTransformationOperationsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listTransformationOperations>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListTransformationOperationsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listTransformationOperations>>> = ({ signal }) =>
    listTransformationOperations(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listTransformationOperations>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListTransformationOperationsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listTransformationOperations>>
>;
export type ListTransformationOperationsQueryError = ErrorType<TLHTTPValidationError>;

export function useListTransformationOperations<
  TData = Awaited<ReturnType<typeof listTransformationOperations>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | ListTransformationOperationsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listTransformationOperations>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listTransformationOperations>>,
          TError,
          Awaited<ReturnType<typeof listTransformationOperations>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListTransformationOperations<
  TData = Awaited<ReturnType<typeof listTransformationOperations>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListTransformationOperationsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listTransformationOperations>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listTransformationOperations>>,
          TError,
          Awaited<ReturnType<typeof listTransformationOperations>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListTransformationOperations<
  TData = Awaited<ReturnType<typeof listTransformationOperations>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListTransformationOperationsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listTransformationOperations>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Transformation Operations
 */

export function useListTransformationOperations<
  TData = Awaited<ReturnType<typeof listTransformationOperations>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListTransformationOperationsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listTransformationOperations>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListTransformationOperationsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List Transformation Operations
 */
export const prefetchListTransformationOperationsQuery = async <
  TData = Awaited<ReturnType<typeof listTransformationOperations>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: ListTransformationOperationsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listTransformationOperations>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListTransformationOperationsQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List Transformation Operations
 */
export const invalidateListTransformationOperations = async (
  queryClient: QueryClient,
  params?: ListTransformationOperationsParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getListTransformationOperationsQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary Run Single Transformation Operation
 */
export const runSingleTransformationOperation = (
  { viewId }: RunSingleTransformationOperationPathParameters,
  tLRunOperationRequest: BodyType<TLRunOperationRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLRunOperationResponse>(
    {
      url: `/view-remediation/transformations/${viewId}/operations/run`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLRunOperationRequest,
      signal,
    },
    options,
  );
};

export const getRunSingleTransformationOperationQueryKey = (
  { viewId }: RunSingleTransformationOperationPathParameters,
  tLRunOperationRequest?: BodyType<TLRunOperationRequest>,
) => {
  return ['POST', 'view-remediation', 'transformations', viewId, 'operations', 'run', tLRunOperationRequest] as const;
};

export const getRunSingleTransformationOperationQueryOptions = <
  TData = Awaited<ReturnType<typeof runSingleTransformationOperation>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { viewId }: RunSingleTransformationOperationPathParameters,
  tLRunOperationRequest: BodyType<TLRunOperationRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof runSingleTransformationOperation>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getRunSingleTransformationOperationQueryKey({ viewId }, tLRunOperationRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof runSingleTransformationOperation>>> = ({ signal }) =>
    runSingleTransformationOperation({ viewId }, tLRunOperationRequest, requestOptions, signal);

  return { queryKey, queryFn, enabled: viewId !== null && viewId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof runSingleTransformationOperation>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type RunSingleTransformationOperationQueryResult = NonNullable<
  Awaited<ReturnType<typeof runSingleTransformationOperation>>
>;
export type RunSingleTransformationOperationQueryError = ErrorType<TLHTTPValidationError>;

export function useRunSingleTransformationOperation<
  TData = Awaited<ReturnType<typeof runSingleTransformationOperation>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RunSingleTransformationOperationPathParameters,
  tLRunOperationRequest: BodyType<TLRunOperationRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof runSingleTransformationOperation>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof runSingleTransformationOperation>>,
          TError,
          Awaited<ReturnType<typeof runSingleTransformationOperation>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRunSingleTransformationOperation<
  TData = Awaited<ReturnType<typeof runSingleTransformationOperation>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RunSingleTransformationOperationPathParameters,
  tLRunOperationRequest: BodyType<TLRunOperationRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof runSingleTransformationOperation>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof runSingleTransformationOperation>>,
          TError,
          Awaited<ReturnType<typeof runSingleTransformationOperation>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRunSingleTransformationOperation<
  TData = Awaited<ReturnType<typeof runSingleTransformationOperation>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RunSingleTransformationOperationPathParameters,
  tLRunOperationRequest: BodyType<TLRunOperationRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof runSingleTransformationOperation>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Run Single Transformation Operation
 */

export function useRunSingleTransformationOperation<
  TData = Awaited<ReturnType<typeof runSingleTransformationOperation>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { viewId }: RunSingleTransformationOperationPathParameters,
  tLRunOperationRequest: BodyType<TLRunOperationRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof runSingleTransformationOperation>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getRunSingleTransformationOperationQueryOptions({ viewId }, tLRunOperationRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Run Single Transformation Operation
 */
export const prefetchRunSingleTransformationOperationQuery = async <
  TData = Awaited<ReturnType<typeof runSingleTransformationOperation>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { viewId }: RunSingleTransformationOperationPathParameters,
  tLRunOperationRequest: BodyType<TLRunOperationRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof runSingleTransformationOperation>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getRunSingleTransformationOperationQueryOptions({ viewId }, tLRunOperationRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Run Single Transformation Operation
 */
export const invalidateRunSingleTransformationOperation = async (
  queryClient: QueryClient,
  { viewId }: RunSingleTransformationOperationPathParameters,
  tLRunOperationRequest: BodyType<TLRunOperationRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getRunSingleTransformationOperationQueryKey({ viewId }, tLRunOperationRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Create View Parity Check
 */
export const createViewParityCheck = (
  { viewId }: CreateViewParityCheckPathParameters,
  tLViewParityCheckRequest: BodyType<TLViewParityCheckRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLViewParityCheckResponse>(
    {
      url: `/view-remediation/transformations/${viewId}/parity-checks`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLViewParityCheckRequest,
      signal,
    },
    options,
  );
};

export const getCreateViewParityCheckQueryKey = (
  { viewId }: CreateViewParityCheckPathParameters,
  tLViewParityCheckRequest?: BodyType<TLViewParityCheckRequest>,
) => {
  return ['POST', 'view-remediation', 'transformations', viewId, 'parity-checks', tLViewParityCheckRequest] as const;
};

export const getCreateViewParityCheckQueryOptions = <
  TData = Awaited<ReturnType<typeof createViewParityCheck>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { viewId }: CreateViewParityCheckPathParameters,
  tLViewParityCheckRequest: BodyType<TLViewParityCheckRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createViewParityCheck>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getCreateViewParityCheckQueryKey({ viewId }, tLViewParityCheckRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof createViewParityCheck>>> = ({ signal }) =>
    createViewParityCheck({ viewId }, tLViewParityCheckRequest, requestOptions, signal);

  return { queryKey, queryFn, enabled: viewId !== null && viewId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof createViewParityCheck>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type CreateViewParityCheckQueryResult = NonNullable<Awaited<ReturnType<typeof createViewParityCheck>>>;
export type CreateViewParityCheckQueryError = ErrorType<TLHTTPValidationError>;

export function useCreateViewParityCheck<
  TData = Awaited<ReturnType<typeof createViewParityCheck>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CreateViewParityCheckPathParameters,
  tLViewParityCheckRequest: BodyType<TLViewParityCheckRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof createViewParityCheck>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof createViewParityCheck>>,
          TError,
          Awaited<ReturnType<typeof createViewParityCheck>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateViewParityCheck<
  TData = Awaited<ReturnType<typeof createViewParityCheck>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CreateViewParityCheckPathParameters,
  tLViewParityCheckRequest: BodyType<TLViewParityCheckRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createViewParityCheck>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof createViewParityCheck>>,
          TError,
          Awaited<ReturnType<typeof createViewParityCheck>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateViewParityCheck<
  TData = Awaited<ReturnType<typeof createViewParityCheck>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CreateViewParityCheckPathParameters,
  tLViewParityCheckRequest: BodyType<TLViewParityCheckRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createViewParityCheck>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Create View Parity Check
 */

export function useCreateViewParityCheck<
  TData = Awaited<ReturnType<typeof createViewParityCheck>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { viewId }: CreateViewParityCheckPathParameters,
  tLViewParityCheckRequest: BodyType<TLViewParityCheckRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createViewParityCheck>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCreateViewParityCheckQueryOptions({ viewId }, tLViewParityCheckRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Create View Parity Check
 */
export const prefetchCreateViewParityCheckQuery = async <
  TData = Awaited<ReturnType<typeof createViewParityCheck>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { viewId }: CreateViewParityCheckPathParameters,
  tLViewParityCheckRequest: BodyType<TLViewParityCheckRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createViewParityCheck>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCreateViewParityCheckQueryOptions({ viewId }, tLViewParityCheckRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Create View Parity Check
 */
export const invalidateCreateViewParityCheck = async (
  queryClient: QueryClient,
  { viewId }: CreateViewParityCheckPathParameters,
  tLViewParityCheckRequest: BodyType<TLViewParityCheckRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getCreateViewParityCheckQueryKey({ viewId }, tLViewParityCheckRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Run View Transformation Workflow
 */
export const runViewTransformationWorkflow = (
  { viewId }: RunViewTransformationWorkflowPathParameters,
  tLRunViewWorkflowRequest: BodyType<TLRunViewWorkflowRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLDispatchViewWorkflowResponse>(
    {
      url: `/view-remediation/transformations/${viewId}/workflows/run`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLRunViewWorkflowRequest,
      signal,
    },
    options,
  );
};

export const getRunViewTransformationWorkflowQueryKey = (
  { viewId }: RunViewTransformationWorkflowPathParameters,
  tLRunViewWorkflowRequest?: BodyType<TLRunViewWorkflowRequest>,
) => {
  return ['POST', 'view-remediation', 'transformations', viewId, 'workflows', 'run', tLRunViewWorkflowRequest] as const;
};

export const getRunViewTransformationWorkflowQueryOptions = <
  TData = Awaited<ReturnType<typeof runViewTransformationWorkflow>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { viewId }: RunViewTransformationWorkflowPathParameters,
  tLRunViewWorkflowRequest: BodyType<TLRunViewWorkflowRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof runViewTransformationWorkflow>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getRunViewTransformationWorkflowQueryKey({ viewId }, tLRunViewWorkflowRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof runViewTransformationWorkflow>>> = ({ signal }) =>
    runViewTransformationWorkflow({ viewId }, tLRunViewWorkflowRequest, requestOptions, signal);

  return { queryKey, queryFn, enabled: viewId !== null && viewId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof runViewTransformationWorkflow>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type RunViewTransformationWorkflowQueryResult = NonNullable<
  Awaited<ReturnType<typeof runViewTransformationWorkflow>>
>;
export type RunViewTransformationWorkflowQueryError = ErrorType<TLHTTPValidationError>;

export function useRunViewTransformationWorkflow<
  TData = Awaited<ReturnType<typeof runViewTransformationWorkflow>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RunViewTransformationWorkflowPathParameters,
  tLRunViewWorkflowRequest: BodyType<TLRunViewWorkflowRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof runViewTransformationWorkflow>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof runViewTransformationWorkflow>>,
          TError,
          Awaited<ReturnType<typeof runViewTransformationWorkflow>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRunViewTransformationWorkflow<
  TData = Awaited<ReturnType<typeof runViewTransformationWorkflow>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RunViewTransformationWorkflowPathParameters,
  tLRunViewWorkflowRequest: BodyType<TLRunViewWorkflowRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof runViewTransformationWorkflow>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof runViewTransformationWorkflow>>,
          TError,
          Awaited<ReturnType<typeof runViewTransformationWorkflow>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRunViewTransformationWorkflow<
  TData = Awaited<ReturnType<typeof runViewTransformationWorkflow>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RunViewTransformationWorkflowPathParameters,
  tLRunViewWorkflowRequest: BodyType<TLRunViewWorkflowRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof runViewTransformationWorkflow>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Run View Transformation Workflow
 */

export function useRunViewTransformationWorkflow<
  TData = Awaited<ReturnType<typeof runViewTransformationWorkflow>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { viewId }: RunViewTransformationWorkflowPathParameters,
  tLRunViewWorkflowRequest: BodyType<TLRunViewWorkflowRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof runViewTransformationWorkflow>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getRunViewTransformationWorkflowQueryOptions({ viewId }, tLRunViewWorkflowRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Run View Transformation Workflow
 */
export const prefetchRunViewTransformationWorkflowQuery = async <
  TData = Awaited<ReturnType<typeof runViewTransformationWorkflow>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { viewId }: RunViewTransformationWorkflowPathParameters,
  tLRunViewWorkflowRequest: BodyType<TLRunViewWorkflowRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof runViewTransformationWorkflow>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getRunViewTransformationWorkflowQueryOptions({ viewId }, tLRunViewWorkflowRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Run View Transformation Workflow
 */
export const invalidateRunViewTransformationWorkflow = async (
  queryClient: QueryClient,
  { viewId }: RunViewTransformationWorkflowPathParameters,
  tLRunViewWorkflowRequest: BodyType<TLRunViewWorkflowRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getRunViewTransformationWorkflowQueryKey({ viewId }, tLRunViewWorkflowRequest) },
    options,
  );

  return queryClient;
};
